import { Injectable } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { saveAs } from 'file-saver';
import { Message } from 'primeng/api';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Router } from '../../../../node_modules/@angular/router';
import { AuthService } from '../../core/auth/auth.service';
import { BackendService } from '../../core/backend.service';
import { BlobBackendService } from '../../core/blob-backend.service';
import { CONFIG } from '../../core/config';
import { ApiResult } from '../../core/messages/api-result';
import { DisplayMessage } from '../../core/messages/display-message';
import { Severity } from '../../shared/enums/severity';
import { MediaAttachmentType, MediaItemDetails, MediaType } from '../../viewmodels/media-item-details';
import { DocumentDownloadRequest } from '../../viewmodels/media/document-downlaod-request';
import { Media, MediaEntityType } from '../../viewmodels/media/media';
import { DynamicDetailsListItemAdapter } from '../dynamic-details-list/dynamic-details-list-item';
import { DynamicDetailsListItemRowData } from '../dynamic-details-list/dynamic-details-list-item-row-data';

@Injectable()
export class MediaItemService {
  allowSave = new Subject<boolean>();
  allowDelete = new Subject<boolean>();
  allowEditDrawing = new Subject<boolean>();
  saveMedia = new Subject();
  deleteMedia = new Subject();
  dataCommitted = new Subject<any>();
  downloadMessages = new Subject<Message[]>();
  hasMedia = new Subject<boolean>();
  media: Media;

  constructor(
    private backendService: BackendService,
    private blobBackendService: BlobBackendService,
    private router: Router,
    private i18NextPipe: I18NextPipe,
    private authService: AuthService,
  ) { }

  setAllowSaveStatus(allowSave: boolean) {
    this.allowSave.next(allowSave);
  }

  setAllowDeleteStatus(allowDelete: boolean) {
    this.allowDelete.next(allowDelete);
  }

  setAllowEditDrawingStatus(allowEditDrawing: boolean) {
    this.allowEditDrawing.next(allowEditDrawing);
  }

  saveMediaItem() {
    this.saveMedia.next();
  }

  deleteMediaItem() {
    this.deleteMedia.next();
  }

  setDataCommitted() {
    this.dataCommitted.next();
  }

  setAttachmentProperties(fileInput) {
    if (this.media.mediaItemDetails.attachmentType === MediaAttachmentType.FileLink) {
      this.media.mediaItemDetails.fileExtension = this.getFileExtension(this.media.mediaItemDetails.fileLinkAddress);
      return;
    }

    const fi = fileInput.nativeElement;

    if (fi.files && fi.files[0]) {
      const selectedFile: File = fi.files[0];

      this.media.mediaItemDetails.fileExtension = this.getFileExtension(selectedFile.name);
      this.setMediaType(selectedFile);
    }
  }

  private getFileExtension(fileName: string): string {
    const fileExtension: string = fileName.substring(fileName.lastIndexOf('.'));

    return fileExtension;
  }

  private setMediaType(selectedFile: File) {
    const contentType: string = selectedFile.type.toString();

    if (contentType.includes('video')) {
      this.media.mediaItemDetails.mediaType = MediaType.Video;
    } else if (contentType.includes('image')) {
      this.media.mediaItemDetails.mediaType = MediaType.Image;
    } else if (contentType.includes('pdf')) {
      this.media.mediaItemDetails.mediaType = MediaType.Pdf;
    } else {
      this.media.mediaItemDetails.mediaType = MediaType.Other;
    }
  }

  uploadFile(fileInput: any, newMedia: Media, isNew: boolean = true): Observable<File> {
    let apiRoute = 'documentupload';

    if (fileInput.nativeElement.files && fileInput.nativeElement.files[0]) {
      const fileExtension = this.getFileExtension(fileInput.nativeElement.files[0].name);
      if (fileExtension === '.dwg') {
        apiRoute = 'drawingupload';
      }
    }

    return this.uploadDocument(newMedia.mediaItemDetails, fileInput, apiRoute, isNew).pipe(map(result => {
      this.media = newMedia;
      return result;
    }));
  }

  replaceDrawingFile(mediaItemDetails: MediaItemDetails, fileInput: any): Observable<File> {
    return this.uploadDocument(mediaItemDetails, fileInput, 'drawingupload', false);
  }

  private uploadDocument(mediaItemDetails: MediaItemDetails, fileInput: any, apiRoute: string, isNew: boolean): Observable<File> {
    const fi = fileInput.nativeElement;

    if (fi.files && fi.files[0]) {
      const fileToUpload: File = fi.files[0];
      const fileExtension = this.getFileExtension(fileToUpload.name);

      if (isNew) {
        return this.backendService.uploadDocument(mediaItemDetails.mediaItemDetailsId, mediaItemDetails.name + fileExtension, fileToUpload, apiRoute).pipe(map(result => {
          this.dataCommitted.next(mediaItemDetails.mediaItemDetailsId);
          return result;
        }));
      } else {
        return this.backendService.updateDocument(mediaItemDetails.mediaItemDetailsId, mediaItemDetails.name + fileExtension, fileToUpload, apiRoute).pipe(map(result => {
          this.dataCommitted.next(mediaItemDetails.mediaItemDetailsId);
          return result;
        }));
      }
    }
  }

  createMediaItem(entityId: string, entityType: MediaEntityType): DynamicDetailsListItemAdapter {
    const newMediaItemDetails = new Media();

    newMediaItemDetails.entityId = entityId;
    newMediaItemDetails.entityType = entityType;

    return new DynamicDetailsListItemAdapter(newMediaItemDetails.mediaItemDetails.name, newMediaItemDetails, [
      new DynamicDetailsListItemRowData(this.i18NextPipe.transform('AddMediaAttachmentList.Name'), newMediaItemDetails.mediaItemDetails.name),
      new DynamicDetailsListItemRowData(this.i18NextPipe.transform('AddMediaAttachmentList.Date'), newMediaItemDetails.mediaItemDetails.attachedDate),
      new DynamicDetailsListItemRowData(this.i18NextPipe.transform('AddMediaAttachmentList.ItemLocation'), newMediaItemDetails.mediaItemDetails.location),
    ]);
  }

  /**
   * Downloads a document to the browser and saves it using the file name details in the mediaItemDetails parameter
   * @param mediaItemDetails object containing document details
   */
  saveDocument(mediaItemDetails: MediaItemDetails) {
    this.downloadDocument(mediaItemDetails, blob => {
      saveAs(blob, mediaItemDetails.name + mediaItemDetails.fileExtension);
    });
  }

  /**
   * Downloads a document and, if successful, calls the callback function with the resulting blob
   * @param mediaItemDetails details of the media item to download
   * @param callback function to be called on successful download
   */
  downloadDocument(mediaItemDetails: MediaItemDetails, callback: (blob: Blob) => void) {
    const downloadRequest: DocumentDownloadRequest = new DocumentDownloadRequest();
    const messages: Message[] = [];
    downloadRequest.mediaItemDetailsId = mediaItemDetails.mediaItemDetailsId;
    this.blobBackendService.getByObject<DocumentDownloadRequest>(DocumentDownloadRequest, downloadRequest).subscribe(blob => {
      if (blob) {
        callback(blob);
      } else {
        messages.push(new DisplayMessage(this.i18NextPipe.transform('AddMediaAttachmentList.DocumentDownloadMessage'), this.i18NextPipe.transform('AddMediaAttachmentList.ResponseFormat'), Severity.Error));
        this.downloadMessages.next(messages);
      }
    }, (errorResult: ApiResult) => {
      errorResult.messages.forEach(message =>
        messages.push(new DisplayMessage(this.i18NextPipe.transform('AddMediaAttachmentList.DocumentNotCreated'), message.text, message.severity)));
      this.downloadMessages.next(messages);
    });
  }

  performDelete(media: Media): Observable<any> {
    return this.backendService.delete<Media>(Media, media.mediaId).pipe(map(result => result));
  }

  displayDrawingEditor() {
    if (this.media) {
      this.router.navigate([this.authService.urlCatalog + CONFIG.routes.idms.inspectionReportDrawing.root(this.media.mediaItemDetailsId)]);
    }
  }
}
